import { CalendarDays, Columns3, List, Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { DetailTab } from '../../detailTabs'
import { EMPTY_FILTERS, hasActiveFilters, type TaskFilters } from '../../taskFilters'
import { PRIORITIES, TASK_TYPES, type Project } from '../../types'
import { useStaffLookup } from '../../ui'

type Props = { project: Project; value: TaskFilters; onChange: (f: TaskFilters) => void; view: DetailTab; onViewChange: (v: DetailTab) => void }

const VIEWS = [
  { key: 'kanban', icon: Columns3 },
  { key: 'list', icon: List },
  { key: 'calendar', icon: CalendarDays },
] as const

/** Kanban, liste ve takvim görünümlerinin ortak filtre satırı; filtreler görünümler arasında korunur. */
export function TaskFilterBar({ project, value, onChange, view, onViewChange }: Props) {
  const { t } = useTranslation()
  const { nameOf } = useStaffLookup()
  const set = <K extends keyof TaskFilters>(k: K, v: TaskFilters[K]) => onChange({ ...value, [k]: v })
  const all = { value: 'all', label: t('common.all') }
  const sprints = [...new Set(project.tasks.map((x) => x.sprint).filter(Boolean))].sort()

  const field = (id: string, label: string, control: React.ReactNode) => (
    <div className="min-w-0 space-y-1">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      {control}
    </div>
  )

  return (
    <div className="mb-4 flex flex-col gap-3 rounded-xl border bg-card p-3 xl:flex-row xl:items-end shadow-card">
      <div className="grid flex-1 grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {field('f-type', t('projects.tasks.fields.type'), (
          <Select id="f-type" size="sm" value={value.type} onChange={(v) => set('type', v as TaskFilters['type'])} options={[all, ...TASK_TYPES.map((x) => ({ value: x, label: t(`projects.taskType.${x}`) }))]} />
        ))}
        {field('f-inst', t('projects.tasks.fields.customer'), (
          <Select id="f-inst" size="sm" value={value.installationId} onChange={(v) => set('installationId', v)} options={[all, ...project.installations.map((i) => ({ value: i.id, label: i.customer }))]} />
        ))}
        {field('f-assignee', t('projects.tasks.fields.assignee'), (
          <Select
            id="f-assignee"
            size="sm"
            value={value.assigneeId}
            onChange={(v) => set('assigneeId', v)}
            options={[all, { value: 'none', label: t('projects.work.unassigned') }, ...project.team.map((m) => ({ value: m.userId, label: nameOf(m.userId) }))]}
          />
        ))}
        {field('f-priority', t('projects.tasks.fields.priority'), (
          <Select id="f-priority" size="sm" value={value.priority} onChange={(v) => set('priority', v as TaskFilters['priority'])} options={[all, ...PRIORITIES.map((x) => ({ value: x, label: t(`projects.priority.${x}`) }))]} />
        ))}
        {field('f-sprint', t('projects.tasks.fields.sprint'), (
          <Select id="f-sprint" size="sm" value={value.sprint} onChange={(v) => set('sprint', v)} options={[all, ...sprints.map((s) => ({ value: s, label: s }))]} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 xl:w-56 xl:flex-none">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input value={value.q} onChange={(e) => set('q', e.target.value)} placeholder={t('projects.tasks.searchPlaceholder')} aria-label={t('projects.tasks.searchPlaceholder')} className="h-8 pl-8" />
        </div>
        {hasActiveFilters(value) ? (
          <Button size="sm" variant="ghost" onClick={() => onChange(EMPTY_FILTERS)}>
            <X />
            {t('common.clear')}
          </Button>
        ) : null}
        <div className="flex rounded-md border p-0.5" role="group" aria-label={t('projects.detail.view')}>
          {VIEWS.map(({ key, icon: Icon }) => (
            <button
              key={key}
              type="button"
              aria-pressed={view === key}
              onClick={() => onViewChange(key)}
              className={cn(
                'inline-flex h-7 items-center gap-1.5 rounded-sm px-2.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                view === key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              <Icon className="size-3.5" aria-hidden />
              <span className="hidden sm:inline">{t(`projects.detail.tabs.${key}`)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
